import { ArrowRight, Eye, EyeOff, Lock, User } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { userLogin } from "../remote";
import { WELCOME_PATH, setMustChangePassword } from "./firstLogin";
import { deriveRole } from './deriveRole';

const Login = () => {
    const { setToken, setRole, setUsername } = useAuth();
    const navigate = useNavigate();

    const [formdata, setFormdata] = useState({
        username: '',
        password: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [remember, setRemember] = useState<boolean>(() => {
        try {
            return localStorage.getItem('auth_remember') !== '0';
        } catch {
            return true;
        }
    });
    const [showPassword, setShowPassword] = useState(false);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormdata((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        const username = formdata.username?.trim();
        const password = formdata.password;
        if (!username || !password) {
            setError('Please enter both username and password.');
            return;
        }

        setLoading(true);
        try {
            const response = await userLogin({ username, password });

            if (response && (response as any).access) {
                const access = (response as any).access;
                const refresh = (response as any).refresh;

                try {
                    localStorage.setItem('auth_remember', remember ? '1' : '0');
                } catch {}

                if (refresh) {
                    try {
                        if (remember) {
                            localStorage.setItem('refresh', refresh);
                            sessionStorage.removeItem('refresh');
                        } else {
                            sessionStorage.setItem('refresh', refresh);
                            localStorage.removeItem('refresh');
                        }
                    } catch {}
                }

                let derivedRole: string | null = null;
                try {
                    const role = deriveRole(response);
                    if (role) {
                        try { localStorage.setItem('auth_role', role); } catch {}
                        setRole(role as any);
                        derivedRole = role;
                    } else {
                        try { localStorage.removeItem('auth_role'); } catch {}
                        setRole(null as any);
                        derivedRole = null;
                    }
                } catch {
                    derivedRole = null;
                }

                // Capture username from JWT response (MyTokenObtainPairView returns `user_name`)
                try {
                    const uname = (response as any).user_name;
                    if (uname) setUsername(uname);
                } catch {}

                setToken(access);

                // First sign-in with a temporary password: replace it (and, for school admins, set up the school).
                const mustChange = !!(response as any).must_change_password && derivedRole !== 'superuser';
                setMustChangePassword(mustChange);
                if (mustChange) {
                    navigate(WELCOME_PATH, { replace: true });
                    return;
                }

                try {
                    if (derivedRole === 'superuser') {
                        navigate('/admin', { replace: true });
                    } else if (derivedRole === 'college_university' || derivedRole === 'college' || derivedRole === 'university') {
                        navigate('/exams/exam-list', { replace: true });
                    } else {
                        navigate('/dashboard', { replace: true });
                    }
                } catch {
                    navigate('/dashboard', { replace: true });
                }
            } else {
                setError('Invalid username or password.');
            }
        } catch (e: any) {
            if (!e?.response) {
                setError('Network error. Please check your internet connection and try again.');
            } else {
                const status = e.response.status;
                const detail = e.response.data?.detail;
                if (typeof detail === 'string' && detail.trim()) {
                    setError(detail);
                } else if (status === 401) {
                    setError('Invalid username or password.');
                } else if (status >= 500) {
                    setError('Server error. Please try again later.');
                } else {
                    setError('Request failed. Please try again.');
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const inputRow = "flex min-h-[48px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-accent/70";
    const inputClass = "min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] text-ink shadow-none outline-none ring-0 focus:ring-0";

    return (
        <div>
            <h1 className="m-0 font-display text-[34px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink">Sign in</h1>
            <p className="mb-[30px] mt-2.5 text-[15px] text-muted">Use the account your school administrator gave you.</p>

            {error && (
                <div role="alert" className="mb-4 rounded-[12px] bg-danger-bg px-4 py-3 text-sm font-medium text-danger-ink">
                    {error}
                </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5" noValidate>
                <div>
                    <label htmlFor="login-username" className="mb-2 block font-semibold text-ink">Username</label>
                    <div className={inputRow}>
                        <User size={18} strokeWidth={1.8} className="text-muted" aria-hidden />
                        <input
                            id="login-username"
                            type="text"
                            name="username"
                            autoComplete="username"
                            autoCapitalize="none"
                            value={formdata.username}
                            onChange={handleInputChange}
                            placeholder="e.g. selam.a"
                            className={inputClass}
                        />
                    </div>
                </div>

                {/* The link follows the password field in tab order but sits in the label row visually. */}
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2">
                    <label htmlFor="login-password" className="order-1 mb-2 font-semibold text-ink">Password</label>
                    <div className={`order-3 col-span-2 ${inputRow}`}>
                        <Lock size={18} strokeWidth={1.8} className="text-muted" aria-hidden />
                        <input
                            id="login-password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            autoComplete="current-password"
                            value={formdata.password}
                            onChange={handleInputChange}
                            placeholder="••••••••"
                            className={inputClass}
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            className="grid h-9 w-9 place-items-center rounded-[8px] bg-transparent p-0 text-muted hover:bg-track"
                        >
                            {showPassword ? <EyeOff size={18} strokeWidth={1.8} aria-hidden /> : <Eye size={18} strokeWidth={1.8} aria-hidden />}
                        </button>
                    </div>
                    <Link to="/auth/forgot-password" className="order-2 mb-2 text-[13px] font-semibold text-ink hover:underline">
                        Forgot password?
                    </Link>
                </div>

                <div className="mt-[18px] flex min-h-[32px] items-center gap-2.5">
                    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ink">
                        <input
                            type="checkbox"
                            className="h-[18px] w-[18px] accent-navy"
                            checked={remember}
                            onChange={(e) => setRemember(e.target.checked)}
                        />
                        Keep me signed in on this device
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="mt-[22px] flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-navy text-[15px] font-semibold text-white hover:text-accent disabled:opacity-60"
                >
                    {loading ? 'Signing in…' : 'Sign in'}
                    <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
                </button>
            </form>

            <div className="mt-7 flex flex-wrap gap-1.5 border-t border-line-strong pt-[22px]">
                <span className="mr-1 self-center text-xs text-muted">For</span>
                {['School admins', 'Teachers', 'Counselors'].map((r) => (
                    <span key={r} className="rounded-full bg-mist px-2.5 py-[5px] text-xs font-semibold text-muted-2">{r}</span>
                ))}
            </div>
        </div>
    );
};

export default Login;
